import React, { useState, useRef, useEffect, useMemo, useCallback, useImperativeHandle } from 'react';
import type { Ref } from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { componentService, moduleService, shopService } from '@/services';
import type { ComponentData } from '@/services';
import type { Gateway, Shop } from '@core/types';
import { useNotifications } from '@core/hooks/useNotifications.ts';
import { useSettingsStore, useApiStore, useAuthStore } from '@core/store';
import { ActiveLicenses } from '@core/types';
import FormContainer, { FormContainerRef } from '@/components/form/FormContainer.tsx';
import FormTextField from '@/components/form/FormTextField.tsx';
import FormSelect from '@/components/form/FormSelect.tsx';
import FormComboBox from '@/components/form/FormComboBox.tsx';
import { LoadingOverlay } from '@/components/shared/LoadingOverlay.tsx';
import { WcnButton, WcnTextField } from '@/components';
import {
    validateName,
    validateIpAddress,
    validatePort,
    required,
    requiredArray,
    validateTxPowerInDbm,
    validateRxSensitivityInDbm,
    nameValidationRules,
    cidRules
} from '@core/utils/validation.ts';
import ToggleButton from '@/components/shared/ToggleButton.tsx';

export interface HardwareComponentEditorRef {
    isDirty: boolean;
    save: () => Promise<boolean>;
}

interface HardwareComponentEditorProps {
    component: ComponentData;
    shopId: string;
    shop: Shop;
    onSave: () => void;
    onCancel: () => void;
    onDelete?: (component: ComponentData) => void;
    ref?: Ref<HardwareComponentEditorRef>;
}

const HARDWARE_TYPE_DEFS = [
    { value: 'Gateway', license: null, label: 'gateway' },
    { value: 'HybridLoc', license: ActiveLicenses.HybridLoc, label: 'hybridLoc' },
    { value: 'FastLaner', license: ActiveLicenses.FastLaner, label: 'fastLaner' },
    { value: 'SensorCamera', license: ActiveLicenses.ShopAutomation, label: 'sensorCamera' },
    { value: 'ImpinjRfidReader', license: ActiveLicenses.Rfid, label: 'Impinj RFID Reader' },
    { value: 'SnmpDevice', license: null, label: 'snmpDevice' },
    { value: 'MediaPlayer', license: ActiveLicenses.ShopAutomation, label: 'mediaPlayer' }
] as const;

const SHOP_BACKED_TYPES = ['HybridLoc', 'FastLaner'];
const isComponentType = (discriminator?: string) =>
    !!discriminator && discriminator !== 'Gateway' && !SHOP_BACKED_TYPES.includes(discriminator);

export const createEmptyComponent = (shopId: string, discriminator = ''): ComponentData => ({
    name: '',
    description: '',
    ipAddress: '',
    port: 0,
    discriminator,
    shopId,
    gatewayId: null,
    lines: [],
    zones: [],
    txPowerInDbm: 20,
    rxSensitivityInDbm: -50,
    disabledAntennas: [],
    type: '',
    resetTimeoutInMs: 0,
    sensorCameraType: '',

    // Gateway
    deviceId: '',
    disabled: false
});

const SENSOR_CAMERA_TYPES = [
    { key: 'Single Sensor', value: 'Singlesensor' },
    { key: 'Multi Sensor', value: 'Multisensor' }
];
const TX_POWER_RANGE = Array.from({ length: 21 }, (_, i) => 10 + i);
const RX_SENSITIVITY_RANGE = Array.from({ length: 81 }, (_, i) => -80 + i);

const FieldRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Grid item container xs={12}>
        <Grid item xs={10} md={6}>
            {children}
        </Grid>
    </Grid>
);

const HardwareComponentEditor: React.FC<HardwareComponentEditorProps> = ({
    component: initialComponent,
    shopId,
    shop,
    onSave,
    onCancel,
    onDelete,
    ref
}) => {
    const { t } = useTranslation();
    const { notifyErrorResponse, notifySuccess, notifyWarning } = useNotifications();
    const { isMobile } = useSettingsStore();
    const { hasLicense } = useAuthStore();
    const { gatewayIds, loadGatewayIds, loadComponentIds } = useApiStore();

    const formRef = useRef<FormContainerRef>(null);
    const [baseline, setBaseline] = useState<ComponentData>(initialComponent);
    const [component, setComponent] = useState<ComponentData>(initialComponent);
    const [loading, setLoading] = useState(false);
    const [formValid, setFormValid] = useState(true);

    const isNew = !initialComponent.id;
    const isShopBackedEdit = SHOP_BACKED_TYPES.includes(initialComponent.discriminator || '');
    const hasGateway = !!shop?.gateways?.length;

    const hardwareTypeItems = useMemo(
        () =>
            HARDWARE_TYPE_DEFS.filter((d) => !d.license || hasLicense(d.license)).map((d) => {
                let disabled = !hasGateway && !['Gateway', 'HybridLoc', 'FastLaner'].includes(d.value);
                if (d.value === 'HybridLoc')
                    disabled ||= !!shop?.hybridLocStoreKey && component.discriminator !== 'HybridLoc';
                if (d.value === 'FastLaner')
                    disabled ||= !!shop?.fastLanerGroupIds?.length && component.discriminator !== 'FastLaner';
                return {
                    value: d.value,
                    text: t(d.label),
                    disabled
                };
            }),
        [hasLicense, t, shop, component.discriminator]
    );

    useEffect(() => {
        setBaseline(initialComponent);
        setComponent(initialComponent);
        setFormValid(true);
        formRef.current?.resetValidation();

        if (!initialComponent.id || !isComponentType(initialComponent.discriminator)) return;

        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const full = await componentService.loadComponent(
                    initialComponent.discriminator!,
                    initialComponent.id!
                );
                if (cancelled) return;
                setBaseline(full);
                setComponent(full);
            } catch (error) {
                if (!cancelled) notifyErrorResponse(error);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [initialComponent]);

    const stableStringify = (obj: unknown): string =>
        JSON.stringify(obj, (_key, value) =>
            value && typeof value === 'object' && !Array.isArray(value)
                ? Object.keys(value)
                      .sort()
                      .reduce((acc: Record<string, unknown>, k) => {
                          acc[k] = (value as Record<string, unknown>)[k];
                          return acc;
                      }, {})
                : value
        );

    const isDirty = useMemo(() => stableStringify(component) !== stableStringify(baseline), [component, baseline]);

    const shopGatewayIds = useMemo(
        () => gatewayIds.filter((g) => g.shopId === (component.shopId ?? shopId)),
        [gatewayIds, component.shopId, shopId]
    );

    const handleFieldChange = useCallback((field: string, value: any) => {
        setComponent((prev) => ({ ...prev, [field]: value }));
    }, []);

    const handleTypeChange = useCallback(
        (value: any) => {
            setComponent((prev) => ({
                ...prev,
                discriminator: value,
                ...(value === 'HybridLoc' ? { hybridLocStoreKey: component.hybridLocStoreKey } : {}),
                ...(value === 'FastLaner' ? { fastLanerGroupIds: component.fastLanerGroupIds || [] } : {})
            }));
            formRef.current?.resetValidation();
        },
        [shop]
    );

    const performSave = useCallback(async (): Promise<boolean> => {
        if (loading) return false;
        const isValid = formRef.current?.validate?.() ?? formValid;
        if (!isValid) {
            notifyWarning(formRef.current?.getRequiredWarning() || 'required');
            return false;
        }
        setLoading(true);
        try {
            const discriminator = component.discriminator;

            if (discriminator === 'Gateway') {
                try {
                    const payload: Partial<Gateway> = {
                        id: component.id,
                        shopId: component.shopId || shopId,
                        deviceId: component.deviceId,
                        deviceCid: component.deviceCid,
                        disabled: component.disabled ?? false,
                        modules: (component as any).modules ?? []
                    };

                    if (isNew) {
                        await moduleService.createModule({ kind: 'Gateway', ...payload } as any);
                    } else {
                        await moduleService.updateGateway(component.deviceId!, payload as Gateway);
                    }
                    notifySuccess();
                } catch (error: any) {
                    if (error?.response?.status === 409) {
                        await loadGatewayIds();
                    } else {
                        notifyErrorResponse(error);
                    }
                }
                await loadGatewayIds();
            } else if (SHOP_BACKED_TYPES.includes(discriminator || '')) {
                if (!shop) return false;
                const updatedShop: Shop = {
                    ...shop,
                    ...(discriminator === 'HybridLoc' ? { hybridLocStoreKey: component.hybridLocStoreKey } : {}),
                    ...(discriminator === 'FastLaner' ? { fastLanerGroupIds: component.fastLanerGroupIds } : {})
                };

                await shopService.saveShop(updatedShop);
                notifySuccess();
            } else {
                if (isNew) {
                    await componentService.createComponent({ ...component, shopId: component.shopId || shopId });
                } else {
                    await componentService.updateComponent(discriminator!, component.id!, component);
                }

                notifySuccess();
                await loadComponentIds();
            }
            return true;
        } catch (error) {
            notifyErrorResponse(error);
            return false;
        } finally {
            setLoading(false);
        }
    }, [loading, formValid, component, isNew, shopId, shop]);

    useImperativeHandle(ref, () => ({ isDirty, save: performSave }), [isDirty, performSave]);

    const handleSave = useCallback(async () => {
        const saved = await performSave();
        if (saved) onSave();
    }, [performSave, onSave]);

    return (
        <Paper variant="outlined" sx={{ borderRadius: '3px', overflow: 'visible' }}>
            {/* Section header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
                <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                >
                    {isNew ? t('addHardware') : t('editHardware')}
                </Typography>
            </Box>

            <Box sx={{ padding: isMobile ? '24px 10px' : '42px 100px 24px 180px' }}>
                <FormContainer formRef={formRef} value={formValid} onChange={setFormValid}>
                    <Grid container spacing={4}>
                        {/* Hardware type selector */}
                        <FieldRow>
                            <FormSelect
                                name="discriminator"
                                label={t('hardwareType')}
                                value={component.discriminator || ''}
                                items={hardwareTypeItems}
                                itemText="text"
                                itemValue="value"
                                placeholder={t('selectAType')}
                                onChange={handleTypeChange}
                                disabled={!isNew || isShopBackedEdit}
                                rules={[required(t)]}
                                required
                                sx={{ width: '100%' }}
                            />
                        </FieldRow>

                        {!isNew && isComponentType(component.discriminator) && (
                            <FieldRow>
                                <WcnTextField label={t('device') + '-ID'} value={component.id} disabled fullWidth />
                            </FieldRow>
                        )}

                        {/* Gateway */}
                        {component.discriminator === 'Gateway' && (
                            <>
                                <FieldRow>
                                    <FormTextField
                                        name="deviceId"
                                        label={t('name')}
                                        value={component.deviceId || ''}
                                        onChange={(e) => handleFieldChange('deviceId', e.target.value)}
                                        required
                                        fullWidth
                                        disabled={!isNew}
                                        rules={nameValidationRules(t)}
                                    />
                                </FieldRow>
                                <FieldRow>
                                    <FormTextField
                                        name="deviceCid"
                                        label={`${t('gateway')}-CID`}
                                        value={component.deviceCid || ''}
                                        onChange={(e) => handleFieldChange('deviceCid', e.target.value)}
                                        rules={cidRules(t)}
                                    />
                                </FieldRow>
                                <FieldRow>
                                    <ToggleButton
                                        checked={!component.disabled}
                                        onChange={(value) => handleFieldChange('disabled', !value)}
                                        label={component.disabled ? t('disabled') : t('enabled')}
                                    />
                                </FieldRow>
                            </>
                        )}

                        {/* HybridLoc */}
                        {component.discriminator === 'HybridLoc' && (
                            <FieldRow>
                                <FormTextField
                                    name="hybridLocStoreKey"
                                    label={t('hybridLocStoreKey')}
                                    type="number"
                                    value={component.hybridLocStoreKey ?? ''}
                                    onChange={(e) => handleFieldChange('hybridLocStoreKey', e.target.value)}
                                    fullWidth
                                    rules={[required(t)]}
                                    required
                                />
                            </FieldRow>
                        )}

                        {/* FastLaner */}
                        {component.discriminator === 'FastLaner' && (
                            <Grid item xs={12}>
                                <FormComboBox
                                    name="fastLanerGroupIds"
                                    label={t('fastLanerGroupIds')}
                                    value={component.fastLanerGroupIds || []}
                                    onChange={(vals) => handleFieldChange('fastLanerGroupIds', vals)}
                                    freeSolo
                                    fullWidth
                                    chipPlacement="outside"
                                    rules={[required(t)]}
                                    required
                                />
                            </Grid>
                        )}

                        {isComponentType(component.discriminator) && (
                            <>
                                <FieldRow>
                                    <FormTextField
                                        name="name"
                                        label={t('name')}
                                        value={component.name || ''}
                                        onChange={(e) => handleFieldChange('name', e.target.value)}
                                        required
                                        fullWidth
                                        rules={[validateName(t)]}
                                    />
                                </FieldRow>

                                <FieldRow>
                                    <WcnTextField
                                        label={t('description')}
                                        value={component.description || ''}
                                        onChange={(e) => handleFieldChange('description', e.target.value)}
                                        fullWidth
                                    />
                                </FieldRow>

                                {component.discriminator !== 'SnmpDevice' && (
                                    <FieldRow>
                                        <FormTextField
                                            name="ipAddress"
                                            label={t('ipAddress')}
                                            value={component.ipAddress || ''}
                                            onChange={(e) => handleFieldChange('ipAddress', e.target.value)}
                                            required
                                            fullWidth
                                            rules={[validateIpAddress(t)]}
                                        />
                                    </FieldRow>
                                )}

                                {['MediaPlayer'].includes(component.discriminator || '') && (
                                    <FieldRow>
                                        <FormTextField
                                            name="port"
                                            label={t('port')}
                                            type="number"
                                            value={component.port ?? ''}
                                            onChange={(e) =>
                                                handleFieldChange(
                                                    'port',
                                                    e.target.value === '' ? undefined : Number(e.target.value)
                                                )
                                            }
                                            rules={[validatePort(t)]}
                                            required
                                            fullWidth
                                        />
                                    </FieldRow>
                                )}

                                {/* SnmpDevice */}
                                {component.discriminator === 'SnmpDevice' && (
                                    <FieldRow>
                                        <FormSelect
                                            name="gatewayId"
                                            label={t('gateway')}
                                            value={component.gatewayId || ''}
                                            onChange={(e) => handleFieldChange('gatewayId', e)}
                                            items={shopGatewayIds}
                                            itemText="deviceId"
                                            itemValue="id"
                                            rules={[required(t)]}
                                            required
                                            sx={{ width: '100%' }}
                                        />
                                    </FieldRow>
                                )}

                                {/* MediaPlayer */}
                                {component.discriminator === 'MediaPlayer' && (
                                    <>
                                        <FieldRow>
                                            <FormTextField
                                                name="resetTimeoutInMs"
                                                label={t('resetTimeoutInMs')}
                                                type="number"
                                                value={component.resetTimeoutInMs ?? ''}
                                                onChange={(e) =>
                                                    handleFieldChange(
                                                        'resetTimeoutInMs',
                                                        e.target.value === '' ? undefined : Number(e.target.value)
                                                    )
                                                }
                                                rules={[required(t)]}
                                                required
                                                fullWidth
                                            />
                                        </FieldRow>
                                        <FieldRow>
                                            <FormSelect
                                                name="gatewayId"
                                                label={t('gateway')}
                                                value={component.gatewayId || ''}
                                                onChange={(e) => handleFieldChange('gatewayId', e)}
                                                items={shopGatewayIds}
                                                itemText="deviceId"
                                                itemValue="id"
                                                rules={[required(t)]}
                                                required
                                                sx={{ width: '100%' }}
                                            />
                                        </FieldRow>
                                    </>
                                )}

                                {/* SensorCamera */}
                                {component.discriminator === 'SensorCamera' && (
                                    <>
                                        <FieldRow>
                                            <FormSelect
                                                name="sensorCameraType"
                                                label={t('type')}
                                                value={component.sensorCameraType || ''}
                                                onChange={(e) => handleFieldChange('sensorCameraType', e)}
                                                items={SENSOR_CAMERA_TYPES}
                                                itemText="key"
                                                itemValue="value"
                                                rules={[required(t)]}
                                                required
                                                placeholder={t('selectAType')}
                                                sx={{ width: '100%' }}
                                            />
                                        </FieldRow>
                                        <Grid item xs={12}>
                                            <FormComboBox
                                                name="lines"
                                                label={t('lines')}
                                                value={component.lines || []}
                                                onChange={(vals) => handleFieldChange('lines', vals)}
                                                rules={[requiredArray(t)]}
                                                freeSolo
                                                fullWidth
                                                chipPlacement="outside"
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormComboBox
                                                name="zones"
                                                label={t('zones')}
                                                value={component.zones || []}
                                                onChange={(vals) => handleFieldChange('zones', vals)}
                                                freeSolo
                                                fullWidth
                                                chipPlacement="outside"
                                            />
                                        </Grid>
                                    </>
                                )}

                                {/* ImpinjRfidReader */}
                                {component.discriminator === 'ImpinjRfidReader' && (
                                    <>
                                        <FieldRow>
                                            <FormSelect
                                                name="txPowerInDbm"
                                                label={t('txPowerInDbm')}
                                                value={component.txPowerInDbm ?? 0}
                                                onChange={(e) =>
                                                    handleFieldChange('txPowerInDbm', e === '' ? undefined : Number(e))
                                                }
                                                items={TX_POWER_RANGE.map((val) => ({ key: val, value: val }))}
                                                itemText="key"
                                                itemValue="value"
                                                rules={[validateTxPowerInDbm(TX_POWER_RANGE)(t)]}
                                                sx={{ width: '100%' }}
                                            />
                                        </FieldRow>
                                        <FieldRow>
                                            <FormSelect
                                                name="rxSensitivityInDbm"
                                                label={t('rxSensitivityInDbm')}
                                                value={component.rxSensitivityInDbm ?? 0}
                                                onChange={(e) =>
                                                    handleFieldChange(
                                                        'rxSensitivityInDbm',
                                                        e === '' ? undefined : Number(e)
                                                    )
                                                }
                                                items={RX_SENSITIVITY_RANGE.map((val) => ({ key: val, value: val }))}
                                                itemText="key"
                                                itemValue="value"
                                                rules={[validateRxSensitivityInDbm(RX_SENSITIVITY_RANGE)(t)]}
                                                sx={{ width: '100%' }}
                                            />
                                        </FieldRow>
                                        <Grid item xs={12}>
                                            <FormComboBox
                                                name="disabledAntennas"
                                                label={t('disabledAntennas')}
                                                value={component.disabledAntennas || []}
                                                onChange={(vals) => handleFieldChange('disabledAntennas', vals)}
                                                freeSolo
                                                fullWidth
                                                chipPlacement="outside"
                                            />
                                        </Grid>
                                    </>
                                )}
                            </>
                        )}
                    </Grid>
                </FormContainer>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: isMobile ? 'center' : 'flex-end',
                        alignItems: isMobile ? 'center' : 'unset',
                        gap: 1,
                        mt: 7
                    }}
                >
                    {(!isNew || isShopBackedEdit) && onDelete && (
                        <WcnButton
                            variant="red"
                            onClick={() => onDelete(component)}
                            style={isMobile ? { width: '270px' } : { width: '240px' }}
                        >
                            {t('delete')}
                        </WcnButton>
                    )}
                    <WcnButton variant="red" onClick={onCancel} style={isMobile ? { width: '270px' } : undefined}>
                        {t('cancel')}
                    </WcnButton>
                    <WcnButton
                        variant="primary"
                        onClick={handleSave}
                        disabled={loading || !formValid}
                        style={isMobile ? { width: '270px' } : undefined}
                    >
                        {t('save')}
                    </WcnButton>
                </Box>

                <LoadingOverlay open={loading} />
            </Box>
        </Paper>
    );
};

export default HardwareComponentEditor;
